<script setup lang="ts">
import { onMounted, watch } from "vue";

import {
  intercomMessengerHidden,
  intercomMessengerShown,
  intercomTriggerClicked,
} from "@/analytics/events/intercom";
import { trackEvent } from "@/analytics/track";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import { useMessengerStore } from "@/store/messenger";
import { useZoomStore } from "@/store/zoom";

const store = useMessengerStore();

// When the app zoom changes, update the related property in the document
// (this affects the size of the Intercom Tooltip marker)
watch(
  () => useZoomStore().factor,
  (zoomFactor) => {
    document.documentElement.style.setProperty("--zoom-factor", zoomFactor.toString());
  },
  { immediate: true },
);

onMounted(() => {
  // When loaded, check if there are any passive notifications
  checkForPassiveNotifications();
});

/**
 * Check if there are any passive notifications (eg. unread news stories).
 * Intercom hasn't built any API for this, so we have to query the DOM...
 */
const checkForPassiveNotifications = () => {
  // This is unfortunately the best way to identify Intercom's passive notification badge
  const queryString = "[class^='intercom'] [data-testid='passive-notifications']";
  const passiveNotifications = document.querySelector(queryString);
  store.setPassiveNotification(!!passiveNotifications);
};

const setupListeners = () => {
  // Track (passive) events when the messenger is shown or hidden
  // (we don't know whether the user triggered them or not)
  window.Intercom("onShow", () => {
    trackEvent(intercomMessengerShown());

    // When the user opens the Messenger, reset the passive notification state
    // (Intercom hides their badge when the Messenger is open)
    store.setPassiveNotification(false);
  });

  window.Intercom("onHide", () => {
    trackEvent(intercomMessengerHidden());
    // When the user closes the Messenger, check again if the notifications are still there
    // (after a slight delay, because Intercom doesn't render their badge right away)
    setTimeout(checkForPassiveNotifications, 100);
  });

  // Show our notification badge when Intercom has incoming messages
  window.Intercom("onUnreadCountChange", (count: number) => {
    useMessengerStore().setUnreadMessages(!!count);
  });
};
setupListeners();
</script>

<template>
  <IconButton
    class="messenger-item"
    icon="general/help"
    :text="$t('action.messenger')"
    @click="trackEvent(intercomTriggerClicked())"
  />
  <Transition name="faster-fade">
    <InfoMarker v-if="store.showBadge" level="error" :top="4" :right="4" />
  </Transition>
</template>

<style lang="scss">
.intercom-launcher,
.intercom-launcher-frame,
.intercom-dfosxs,
 // Hide intercom's trigger (unfortunately, the trigger's class changes with every intercom deployment,
 // but it seems to consistently start with "intercom-with-namespace-xxxxxx")
.intercom-app > [class^="intercom-with-namespace-"]:not(.intercom-messenger-frame) {
  display: none !important;
  pointer-events: none !important;
}

// Scale up the Intercom Tooltip marker on the board, depending on the zoom factor
// (when zoomed in, scale it up more)
#boards .intercom-namespace [id^="tooltip-anchor"] svg {
  transform: scale(calc(7 / sqrt(var(--zoom-factor))));
}
</style>
